import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Chip } from '../kit.js';
import { clip } from '../../data/cameras.js';
import { Icon, eventIcon } from './glyphs.js';
import { elapsed, timeOf, whenOf } from './model.js';

/* The current time, refreshed every `ms` while `active`. Kept to the
   small parts that print a running time, so a ticking clock re-renders
   a label rather than a whole screen. */
export function useNow(ms = 1000, active = true) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!active) return undefined;
        setNow(Date.now());
        const timer = setInterval(() => setNow(Date.now()), ms);
        return () => clearInterval(timer);
    }, [ms, active]);

    return now;
}

/* "0:34", counting up from `since`. */
export function Elapsed({ since }) {
    const now = useNow(1000);
    return <span className="acc-num">{elapsed(now - since)}</span>;
}

/* Whole seconds left until `until`, counting down. */
export function Countdown({ until }) {
    const now = useNow(200, until > Date.now());
    return <span className="acc-num">{Math.max(0, Math.ceil((until - now) / 1000))}s</span>;
}

/* A clock with seconds, for the camera overlay. */
export function LiveClock() {
    const now = useNow(1000);
    return <span className="acc-num">{timeOf(now, true)}</span>;
}

/* Where this module's confirmations appear. The access screens are
   long, and a confirmation printed at the top of one is out of sight
   from the credentials or the schedules; this floats the kit's flash
   under the top bar instead, above any open sheet, wherever the reader
   has scrolled to. */
export function Toast({ children }) {
    if (typeof document === 'undefined') return null;
    return createPortal(<div className="acc-toast">{children}</div>, document.body);
}

/* An on/off control that says which it is. */
export function Switch({ checked, onChange, label, disabled }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            className={`acc-switch${checked ? ' acc-switch--on' : ''}`}
            onClick={() => onChange(!checked)}
            disabled={disabled}
        >
            <span className="acc-switch__knob" />
        </button>
    );
}

/* One line of door activity: what happened, where, who, and when.
   The feed prints seconds; a resident's history prints the day. */
export function EventRow({ event, when = 'time', door = true }) {
    const detail = [event.who, event.via, event.note].filter(Boolean).join(' · ');

    return (
        <div className="acc-event" data-tone={event.tone}>
            <span className="acc-event__icon"><Icon name={eventIcon(event)} size={16} /></span>
            <div className="acc-event__main">
                <span className="acc-event__title">
                    {event.title}
                    {door && event.doorName ? <span className="acc-event__door">, {event.doorName}</span> : null}
                </span>
                {detail && <span className="acc-event__meta">{detail}</span>}
            </div>
            <div className="acc-event__end">
                <Chip tone={event.tone}>{event.label}</Chip>
                <time className="acc-event__time">{when === 'day' ? whenOf(event.ts) : timeOf(event.ts, true)}</time>
            </div>
        </div>
    );
}

/* A camera's live picture. The clip is streamed from public/cameras
   (an MP4, plus a WebM only where one exists); if it is missing or will
   not play, the camera's bundled still stands in, so a door is never
   shown beside a black box. */
export function CameraFeed({ camera, children }) {
    const [failed, setFailed] = useState(false);
    const src = clip(camera.key);

    return (
        <div className="acc-cam">
            {failed ? (
                <img className="acc-cam__media" src={camera.still} alt="" />
            ) : (
                <video
                    key={camera.id}
                    className="acc-cam__media"
                    muted
                    autoPlay
                    loop
                    playsInline
                    poster={camera.still}
                    onError={() => setFailed(true)}
                >
                    {src.webm && <source src={src.webm} type="video/webm" />}
                    <source src={src.mp4} type="video/mp4" onError={() => setFailed(true)} />
                </video>
            )}
            <div className="acc-cam__hud">
                <span className="acc-cam__live"><span className="acc-cam__dot" />Live</span>
                <span className="acc-cam__name">{camera.name} · {camera.location}</span>
                <span className="acc-cam__clock"><LiveClock /></span>
            </div>
            {children}
        </div>
    );
}
